"use client"

import { useEffect, useState } from "react"
import { useLocation, useParams, Link, useNavigate } from "react-router-dom"
import { MapPin, Star, ArrowLeft, Heart } from "lucide-react"
import { ref, onValue, set, remove } from "../lib/firebase-database"
import { db } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { MobileBottomNav } from "./MobileBottomNav"

export default function SearchResults() {
  const location = useLocation()
  const { destination } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [villas, setVillas] = useState([])
  const [loading, setLoading] = useState(true)
  const [wishlistedProperties, setWishlistedProperties] = useState({})
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (user) {
      const wishlistRef = ref(db, `wishlists/${user.uid}`)
      onValue(wishlistRef, (snapshot) => setWishlistedProperties(snapshot.val() || {}))
    }
  }, [user])

  useEffect(() => {
    setLoading(true)
    if (location.state?.searchResults) {
      // If coming from search, use the search results
      setVillas(location.state.searchResults)
      setLoading(false)
    } else if (destination) {
      // If accessing via URL, fetch villas for the destination
      const villasRef = ref(db, "villas")
      const unsubscribe = onValue(villasRef, (snapshot) => {
        const data = snapshot.val()
        if (data) {
          const destinationName = destination.replace(/-/g, " ")
          const filteredVillas = Object.entries(data)
            .map(([id, villa]) => ({
              _id: id,
              ...villa,
            }))
            .filter(
              (villa) =>
                villa.mainCity && villa.mainCity.toLowerCase() === destinationName.toLowerCase() && !villa.isHidden,
            )
          setVillas(filteredVillas)
        } else {
          setVillas([])
        }
        setLoading(false)
      })

      // Cleanup subscription
      return () => unsubscribe()
    }
  }, [location.state, destination])

  const handleWishlistClick = async (propertyId) => {
    if (!user) {
      navigate("/login")
      return
    }

    const wasWishlisted = isPropertyWishlisted(propertyId)
    setWishlistedProperties((prev) => {
      const next = { ...prev }
      if (wasWishlisted) delete next[propertyId]
      else next[propertyId] = true
      return next
    })

    const wishlistRef = ref(db, `wishlists/${user.uid}/${propertyId}`)
    try {
      if (wasWishlisted) {
        await remove(wishlistRef)
      } else {
        await set(wishlistRef, true)
      }
    } catch (error) {
      console.error("Wishlist update failed:", error)
      setWishlistedProperties((prev) => {
        const next = { ...prev }
        if (wasWishlisted) next[propertyId] = true
        else delete next[propertyId]
        return next
      })
      alert("Failed to update favorites. Please try again.")
    }
  }

  const isPropertyWishlisted = (propertyId) => !!wishlistedProperties[propertyId]

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EDEFEF] p-4 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  const formattedDestination = destination
    ?.replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">
                {location.state?.searchQuery ? `Search Results` : `Villas in ${formattedDestination}`}
              </h1>
              <p className="text-gray-600 text-sm">
                {villas.length} properties found
                {location.state?.searchQuery ? ` for "${location.state.searchQuery}"` : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 pb-20">
        {location.state?.filters && (
          <div className="mb-4 flex flex-wrap gap-2">
            {location.state.filters.mainCity && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                Location: {location.state.filters.mainCity}
              </span>
            )}
            {location.state.filters.guestCount && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                Guests: {location.state.filters.guestCount}+
              </span>
            )}
            {location.state.filters.maxPrice && (
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                Budget: Under ₹{location.state.filters.maxPrice}
              </span>
            )}
          </div>
        )}

        {villas.length > 0 ? (
          <div className={`grid gap-4 ${isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
            {villas.map((villa) => (
              <div
                key={villa._id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow"
              >
                <Link to={`/villa/${villa._id}`} className="block">
                  <div className="relative h-48">
                    <img
                      src={villa.thumbnail || villa.images?.[0] || "/placeholder.jpg"}
                      alt={villa.name}
                      className="w-full h-full object-cover"
                    />
                    {villa.discount > 0 && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-sm">
                        {villa.discount}% OFF
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        handleWishlistClick(villa._id)
                      }}
                      className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-md hover:scale-110 transition-transform"
                    >
                      {isPropertyWishlisted(villa._id) ? (
                        <Heart className="h-4 w-4 text-red-500 fill-current" />
                      ) : (
                        <Heart className="h-4 w-4 text-gray-600 hover:text-red-500 transition-colors" />
                      )}
                    </button>
                  </div>
                </Link>

                <div className="p-4">
                  <Link to={`/villa/${villa._id}`}>
                    <h2 className="text-lg font-semibold mb-2 hover:text-blue-600 transition-colors">{villa.name}</h2>
                  </Link>
                  <div className="flex items-center text-gray-600 mb-2">
                    <MapPin className="h-4 w-4" />
                    <span className="ml-1 text-sm">{villa.mainCity}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-400" />
                      <span className="ml-1 text-sm">{villa.averageRating?.toFixed(1) || "New"}</span>
                      {villa.reviewCount > 0 && (
                        <span className="text-sm text-gray-500 ml-1">({villa.reviewCount})</span>
                      )}
                    </div>
                    <p className="text-lg font-bold">
                      ₹{villa.rate}
                      <span className="text-sm font-normal text-gray-600">/night</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <MapPin className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No properties found</h3>
            <p className="text-gray-500 mb-4">
              {formattedDestination
                ? `No properties found in ${formattedDestination}`
                : "No properties match your search criteria"}
            </p>
            <button
              onClick={() => navigate("/explore")}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Explore All Properties
            </button>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav user={user} />}
    </div>
  )
}
